import * as vp from 'vopay';

import { useEffect, useMemo, useState } from 'react';
import { withRouter, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import DashboardNavTop from '@/components/UI/DashboardNavTop';
import NavLeftContainer from '@/components/UI/NavLeftContainer/NavLeftContainer';
import DashboardRoutes from '@/components/Homepage/Routes';
import AccountRoutes from '@/components/Accounts/Routes';
import TransactionApprovalRoutes from '@/components/TransactionApproval/Routes';
import SubAccountsRoutes from '@/components/SubAccounts/Routes';
import ClientAccountsRoutes from '@/components/ClientAccounts/Routes';
import UsersRoutes from '@/components/Users/Routes';
import SupportRoutes from '@/components/Support/Routes';
import PaymentRoutes from '@/components/Payment/Routes';
import CollectionRoutes from '@/components/Collection/Routes';
import ConversionRoutes from '@/components/Conversion/Routes';
import ContactsRoutes from '@/components/Contacts/Routes';
import UploadRoutes from '@/components/Upload/Routes';
import SoftwareRoutes from '@/components/Software/Routes';
import TransactionRoutes from '@/components/Transaction/Routes';
import StatementsRoutes from '@/components/Statements/Routes';
import IntegrationsRoutes from '@/components/Intergrations/Routes';
import SettingsRoutes from '@/components/Settings/Routes';
import ElinxRoutes from '@/components/Elinx/ElinxPages/Routes';
import GlobalSendRoutes from '@/components/GlobalSend/Routes';
import DisputeManagementRoutes from '@/components/DisputeManagement/DisputeManagementRoutes';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import useActiveModule from '@/hooks/useActiveModule';
import styles from './Footer.module.scss';
import navBarStyles from './NavBar.module.scss';
import { useBranding } from '@/providers/Branding.provider';
import useHubspotElement from '@/hooks/useHubspotElement';
import useFetchAccountBranding from '@/hooks/useApi/useFetchAccountBranding';
import { useClientData } from '@/providers/ClientData.provider';
import BillPaymentRoutes from '@/components/BillPayment/Routes';
import useTranslationsOverride from '@/hooks/useTranslationsOverride';
import i18n from '@/i18n/i18n';

const { Container, DashboardLayout } = vp;

const Dashboard = withRouter(() => {
  const { t } = useTranslation('translations', { keyPrefix: 'Dashboard' });
  const { pathname } = useLocation();
  const { activeModule } = useActiveModule();
  const { logo, smallLogo, setBrandingValues } = useBranding();
  const { isSuspiciousLogin, logout, setMessageLogout } = useClientLogin();
  const { fetchAccountBranding } = useFetchAccountBranding();
  const { response } = useClientData();
  const { navRef } = useHubspotElement();
  const { translationsInitialized } = useTranslationsOverride();

  const [expandMenu, setExpandMenu] = useState(false);

  useEffect(() => {
    const d = new Date();
    localStorage.setItem('SessionTime', d.getTime().toString());
    const sessionTimout = setInterval(() => {
      const d2 = new Date();
      if (d2.getTime() - Number(window.localStorage.getItem('SessionTime')) > 3600000) {
        setMessageLogout(true);
        logout();
      }
    }, 1000);

    return () => {
      clearInterval(sessionTimout);
    };
  }, []);

  useEffect(() => {
    const handleClick = () => {
      const d = new Date();
      localStorage.setItem('SessionTime', d.getTime().toString());
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    vp.loadScriptByURL('hs-script-loader', '//js.hs-scripts.com/6101456.js')
      .then(() => {
        console.log('HubSpot script loaded');
      })
      .catch((error) => {
        console.error('Failed to load HubSpot script:', error);
      });
  }, []);

  useEffect(() => {
    initBranding();
  }, [response.AccountID]);

  useHubspotElement();

  const handlers = {
    onMouseEnter: (show: boolean) => {
      setExpandMenu(show);
    },
  };

  useEffect(() => {
    if (isSuspiciousLogin) {
      setMessageLogout(true);
      logout();
    }
  }, [isSuspiciousLogin]);

  const [brandingLoaded, setBrandingLoaded] = useState(false);

  async function initBranding() {
    setBrandingLoaded(false);
    const res = await fetchAccountBranding();

    if (!res.Success) {
      console.warn(res.ErrorMessage);
    }

    setBrandingValues({
      primaryColor: res.BrandingDetails.PrimaryColor,
      secondaryColor: res.BrandingDetails.SecondaryColor,
      logo: res.Logo,
      smallLogo: res.SmallLogo,
      appTitle: res.AppTitle,
      appFavicon: res.Favicon,
    });

    setBrandingLoaded(true);
  }

  const isEmbedded = useMemo(
    () => ['global-send', 'dispute-management', 'bill-payment'].includes(pathname.split('/')[1]),
    [pathname],
  );

  if (!brandingLoaded || !translationsInitialized) return null;

  return (
    <DashboardLayout
      handlers={handlers}
      pathname={pathname}
      expandMenu={expandMenu}
      isEmbedded={isEmbedded}
      navSubTop={
        <div>
          {/* <div id="hs-web-interactives-top-anchor" ref={hubspotRef}></div> */}
          <div className={navBarStyles.navbarContainer} ref={navRef}>
            <DashboardNavTop />
          </div>
        </div>
      }
      navLeft={
        <NavLeftContainer
          logo={logo === '' ? undefined : logo}
          smallLogo={smallLogo === '' ? undefined : smallLogo}
          expandMenu={expandMenu}
        />
      }
      content={
        <Container maxWidth="lg" className={`${styles.mainContent} ${isEmbedded && styles.embedded}`}>
          {activeModule === 'dashboard' && <DashboardRoutes />}
          {activeModule === 'myAccount' && <AccountRoutes />}
          {activeModule === 'subAccounts' && <SubAccountsRoutes />}
          {activeModule === 'clientAccounts' && <ClientAccountsRoutes />}
          {activeModule === 'transactionApproval' && <TransactionApprovalRoutes />}
          {activeModule === 'eLinx' && <ElinxRoutes />}
          {activeModule === 'payment' && <PaymentRoutes />}
          {activeModule === 'payment' && <BillPaymentRoutes />}
          {activeModule === 'collection' && <CollectionRoutes />}
          {activeModule === 'conversion' && <ConversionRoutes />}
          {activeModule === 'contacts' && <ContactsRoutes />}
          {activeModule === 'upload' && <UploadRoutes />}
          {activeModule === 'software' && <SoftwareRoutes />}
          {activeModule === 'transactions' && <TransactionRoutes />}
          {activeModule === 'statements' && <StatementsRoutes />}
          {activeModule === 'integrations' && <IntegrationsRoutes />}
          {activeModule === 'settings' && <SettingsRoutes />}
          {activeModule === 'support' && <SupportRoutes />}
          {activeModule === 'users' && <UsersRoutes />}
          {activeModule === 'globalSend' && <GlobalSendRoutes />}
          {activeModule === 'disputeManagement' && <DisputeManagementRoutes />}
        </Container>
      }
      footer={
        i18n.exists('Dashboard.footer.override') ? (
          <div className={styles.footerContainer}>
            <div className={styles.container}>
              <Trans
                i18nKey="footer.override"
                t={t}
                values={{
                  year: new Date().getFullYear(),
                }}
                defaults="defaults"
                components={{
                  a: <a target="_blank" rel="noreferrer" />,
                }}
              />
            </div>
          </div>
        ) : window.location.hostname.includes('vopay') || window.location.hostname.includes('localhost') ? (
          <div className={styles.footerContainer}>
            <div className={styles.container}>
              <Trans
                i18nKey="footer.text"
                t={t}
                values={{
                  year: new Date().getFullYear(),
                }}
                components={{
                  a: <a target="_blank" rel="noreferrer" />,
                }}
              />
            </div>
          </div>
        ) : (
          <></>
        )
      }
    />
  );
});

export default Dashboard;
